import { useCallback, useEffect, useRef } from 'react';

import { CheckoutSessionRequest } from 'types/generated/models/CheckoutSessionRequest';

import useCreateCheckoutSession from './useCreateCheckoutSession';
import useUpdateCheckoutSessionMetadata from './useUpdateCheckoutSessionMetadata';

interface IUseStripeCheckoutSession {
  athleteId: string;
  returnPath: string;
  setAsDefault?: boolean;
  /**
   * The staff card-attach auto-pay toggle. It travels in the session's metadata, which is this
   * route's counterpart to the payment-method metadata the payment-element route sends, and the
   * webhook reads it only alongside `set_as_default`. Off by default, so the callers that never
   * offer the toggle keep the behaviour they have.
   */
  enableAutoPay?: boolean;
}

interface UseStripeCheckoutSessionResult {
  isLoading: boolean;
  isError: boolean;
  clientSecret: string | null;
  handleCreateCheckoutSession: () => Promise<void>;
}

function useStripeCheckoutSession({
  athleteId,
  returnPath,
  setAsDefault = false,
  enableAutoPay = false,
}: IUseStripeCheckoutSession): UseStripeCheckoutSessionResult {
  const {
    mutateAsync: createCheckoutSession,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
    data: checkoutData,
  } = useCreateCheckoutSession();

  const { mutate: updateSessionMetadata } = useUpdateCheckoutSessionMetadata();

  const isCheckoutLoadingRef = useRef(false);
  useEffect(() => {
    isCheckoutLoadingRef.current = isCheckoutLoading;
  }, [isCheckoutLoading]);

  // The pair the session already carries, seeded with what created it. The amend below fires
  // only when the form has moved away from this, which is what lets it depend on the session id
  // without posting a PUT that changes nothing every time a session lands.
  const sessionMetadataRef = useRef<{ setAsDefault: boolean; enableAutoPay: boolean } | null>(null);

  const handleCreateCheckoutSession = useCallback(async (): Promise<void> => {
    if (isCheckoutLoadingRef.current || checkoutData?.data?.clientSecret) return;

    const requestData: CheckoutSessionRequest = {
      athleteId,
      returnPath,
      setAsDefault,
      enableAutoPay,
    };

    sessionMetadataRef.current = { setAsDefault, enableAutoPay };
    await createCheckoutSession(requestData);
  }, [
    athleteId,
    returnPath,
    setAsDefault,
    enableAutoPay,
    createCheckoutSession,
    checkoutData?.data?.clientSecret,
  ]);

  const sessionId = checkoutData?.data?.sessionId;

  // Depending on the session id is the point. The session is always created with
  // enable_auto_pay false, so this call is the only way the flag ever becomes true on this
  // route, and the toggle can move while the create POST is still in flight. Skipping for want
  // of a session therefore has to be followed by a retry once one arrives, or the toggle stays
  // visibly on, the card saves, and auto-pay reaches nothing.
  useEffect(() => {
    if (!sessionId) return;

    const carried = sessionMetadataRef.current;
    if (carried && carried.setAsDefault === setAsDefault && carried.enableAutoPay === enableAutoPay) {
      return;
    }

    // Both options are pushed on either change. Stripe merges what is posted here, so sending
    // the pair keeps the session's copy matching the form even when only one of them moved.
    sessionMetadataRef.current = { setAsDefault, enableAutoPay };
    updateSessionMetadata({
      session_id: sessionId,
      set_as_default: setAsDefault,
      enable_auto_pay: enableAutoPay,
    });
  }, [sessionId, setAsDefault, enableAutoPay, updateSessionMetadata]);

  return {
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
    clientSecret: checkoutData?.data?.clientSecret || null,
    handleCreateCheckoutSession,
  };
}

export default useStripeCheckoutSession;
